import { REMINDERS_PAUSED_REASONS, resolveRemindersPausedReason } from '@shared/core/billing'
import classNames from 'classnames'
import { format } from 'date-fns'
import { FC, useMemo } from 'react'
// components
import { FormattedText, Loader, Table, Typography } from 'src/modules/core/components'
// hooks
import { useInvoices } from 'src/modules/payment/hooks/use-invoices'
import { useSubscription } from 'src/modules/payment/hooks/use-subscription'
import { useUpdatePaymentMethod } from 'src/modules/payment/hooks/use-update-payment-method'
import { useProfile } from 'src/modules/user/hooks/use-profile'
// utils
import { roundNumber } from 'src/modules/core/utils/helpers'
import { isShopifyBilledProfile } from 'src/modules/payment/utils/helpers'
// types
import type { IntlMessageID } from 'src/modules/core/types/i18n.provider.types'
import type { TableColumn, TableData } from 'src/modules/core/types/table.types'
import { SubscriptionStatus } from 'src/modules/payment/types/payment.types'

import classes from './billing-tab.module.scss'

const formatId = (n: number): string => {
  const maxValue = 10

  return n < maxValue ? `#0${n}` : `#${n}`
}

function paymentDetailsMessageId(isPaymentPaused: boolean, state?: string): IntlMessageID {
  if (isPaymentPaused) return 'payment.billing.details.message.paused'
  if (state === SubscriptionStatus.past_due) return 'payment.billing.details.message.failed'

  return 'payment.billing.details.message.succeeded'
}

const tableColumns: TableColumn[] = [
  {
    value: <FormattedText id="payment.billing.table.invoiceId" />,
  },
  {
    value: <FormattedText id="payment.billing.table.description" />,
    width: 256,
  },
  {
    value: <FormattedText id="payment.billing.table.startDate" />,
    width: 145,
  },
  {
    value: <FormattedText id="payment.billing.table.amount" />,
    width: 149,
  },
  {
    value: <FormattedText id="payment.billing.table.status" />,
    width: 213,
  },
]

export const BillingTab: FC = () => {
  // hooks
  const profile = useProfile()
  const isShopifyBilled = isShopifyBilledProfile(profile.data)
  const subscriptionOptions = {
    enabled: !!profile.data?.billing_subscription_id || isShopifyBilled,
  }
  const subscription = useSubscription({}, subscriptionOptions)
  const invoices = useInvoices(undefined, { enabled: !isShopifyBilled })
  const { openUpdateCard } = useUpdatePaymentMethod()
  // memo
  const isLoading = useMemo(
    () => subscription.isLoading || (!isShopifyBilled && invoices.isLoading),
    [subscription.isLoading, invoices.isLoading, isShopifyBilled],
  )

  // Keyed on the reason, not state: Shopify statuses are synthesized as `paused` too.
  const isPaymentPaused =
    !!profile.data && resolveRemindersPausedReason(profile.data) === REMINDERS_PAUSED_REASONS.paymentPaused
  const isLastPaymentSuccessful = !isPaymentPaused && subscription.data?.state !== SubscriptionStatus.past_due

  const tableData: TableData[] = useMemo(() => {
    if (isShopifyBilled || !Array.isArray(invoices.data)) {
      return []
    }

    return [...invoices.data]
      .filter((invoice) => invoice.transaction)
      .sort((a, b) => new Date(b.transaction.created_at).getTime() - new Date(a.transaction.created_at).getTime())
      .map((invoice, index, array) => {
        const isPaid = invoice.transaction.status === 'completed'
        const startDate = new Date(invoice.transaction.created_at)

        return {
          id: formatId(array.length - index),
          description: (
            <FormattedText id="payment.billing.table.description.value" values={{ month: format(startDate, 'LLLL') }} />
          ),
          startDate: format(startDate, 'M/d/yyyy'),
          amount: `$${roundNumber(parseInt(invoice.transaction.amount, 10), 2)}`,
          status: (
            <div
              className={classNames(classes.statusWrapper, {
                [classes.successWrapper]: isPaid,
                [classes.errorWrapper]: !isPaid,
              })}
            >
              <Typography
                variant="span"
                className={classNames(classes.status, {
                  [classes.successText]: isPaid,
                  [classes.errorText]: !isPaid,
                })}
              >
                {isPaid ? 'paid' : 'due'}
              </Typography>
            </div>
          ),
        }
      })
  }, [isShopifyBilled, invoices.data])

  if (isLoading) return <Loader isLoading={isLoading} />

  return (
    <div>
      {subscription?.data?.next_payment && (
        <div className={classes.row}>
          <div className={classes.left}>
            <div className={classes.textWrapper}>
              <Typography variant="span" className={classes.title}>
                <FormattedText id="payment.billing.title.nextCycle" />
              </Typography>
              <Typography variant="span" className={classes.description}>
                <FormattedText id="payment.billing.description.automaticBilling" />
              </Typography>
            </div>
            <Typography variant="body" className={classes.message}>
              ${roundNumber(subscription.data?.next_payment.amount, 2)} on{' '}
              {format(new Date(subscription.data?.next_payment.date), 'EEEE, MMMM d, yyyy')}
            </Typography>
          </div>
        </div>
      )}
      {subscription?.data && (
        <div className={classes.row}>
          <div className={classes.left}>
            <div className={classes.textWrapper}>
              <Typography variant="span" className={classes.title}>
                <FormattedText id="payment.billing.title.details" />
              </Typography>
              <Typography variant="span" className={classes.description}>
                <FormattedText id="payment.billing.description.changeCard" />
              </Typography>
            </div>
            <Typography
              variant="body"
              className={classNames(classes.message, {
                [classes.error]: !isLastPaymentSuccessful,
              })}
            >
              <FormattedText id={paymentDetailsMessageId(isPaymentPaused, subscription.data?.state)} />
            </Typography>
          </div>
          <div className={classes.right}>
            <Typography variant="h4" className={classes.clickableText} onClick={openUpdateCard}>
              <FormattedText id={isShopifyBilled ? 'payment.billing.shopify.manage' : 'settings.button.edit'} />
            </Typography>
          </div>
        </div>
      )}
      {isShopifyBilled ? (
        <Typography variant="body" className={classNames(classes.description, classes.shopifyBanner)}>
          <FormattedText id="payment.billing.shopify.banner" />
        </Typography>
      ) : (
        <>
          <div className={classes.tableTitleWrapper}>
            <Typography variant="span" className={classes.title}>
              <FormattedText id="payment.billing.title.invoices" />
            </Typography>
          </div>
          <Table columns={tableColumns} data={tableData} />
        </>
      )}
    </div>
  )
}
